import { openPaymentPage } from "@/lib/open-checkout";
import { friendlyError } from "@/lib/friendly-errors";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { getShopProduct, createShopCheckout, recordAffiliateClick } from "@/lib/shop.functions";
import { ShopPrice } from "@/components/shop/ShopProductCard";
import { AFFILIATE_DISCLOSURE, categoryLabel } from "@/lib/shop-pricing";
import { useHideCommerce } from "@/lib/native-app";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, ExternalLink, ImageIcon, Loader2, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/shop/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug.replace(/-/g, " ")} — Shop | Recovery Trauma™` },
      {
        name: "description",
        content:
          "A gently chosen item from the Recovery Trauma™ wellbeing shop.",
      },
      { property: "og:title", content: "Recovery Trauma™ Shop" },
      {
        property: "og:description",
        content: "A gently chosen item from the Recovery Trauma™ wellbeing shop.",
      },
      { property: "og:type", content: "product" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `https://recoverytrauma.com/shop/${params.slug}` }],
  }),
  component: ShopProductPage,
});

function ShopProductPage() {
  const { slug } = Route.useParams();
  const fetchProduct = useServerFn(getShopProduct);
  const startCheckout = useServerFn(createShopCheckout);
  const trackClick = useServerFn(recordAffiliateClick);
  const hideCommerce = useHideCommerce();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["shop", "product", slug],
    queryFn: () => fetchProduct({ data: { slug } }),
  });
  const product = data?.product ?? null;

  async function buy() {
    setBusy(true);
    try {
      const res = await startCheckout({
        data: { slug, origin: window.location.origin },
      });
      if ("error" in res && res.error) throw new Error(res.error);
      if (!("url" in res) || !res.url) throw new Error("Checkout could not be opened.");
      openPaymentPage(res.url);
    } catch (err) {
      toast.error(friendlyError(err, "Something went wrong."));
      setBusy(false);
    }
  }

  if (isLoading) {
    return (
      <main className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold">This item isn't available</h1>
        <p className="mt-2 text-muted-foreground">It may have been removed or renamed.</p>
        <Link to="/shop" className="mt-6 inline-block text-primary underline">
          Back to the shop
        </Link>
      </main>
    );
  }

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-12">
      <Link
        to="/shop"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to the shop
      </Link>

      <div className="grid gap-8 md:grid-cols-2">
        <figure className="mx-auto w-full max-w-64 self-start overflow-hidden rounded-2xl border bg-muted md:sticky md:top-24 md:mx-0">
          <div className="aspect-square w-full overflow-hidden">
            {product.image_url ? (
              <img
                src={product.image_url}
                alt={product.title}
                className="block h-full w-full object-contain p-2"
              />
            ) : (
              <div className="flex aspect-square items-center justify-center">
                <ImageIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
              </div>
            )}
          </div>
          {product.is_illustration && (
            <figcaption className="bg-card px-3 py-2 text-xs text-muted-foreground">
              Illustration — not an official product photo.
            </figcaption>
          )}
        </figure>

        <div>
          <h1 className="text-3xl font-semibold">{product.title}</h1>
          {product.summary ? (
            <p className="mt-2 text-muted-foreground">{product.summary}</p>
          ) : null}
          {product.category_slug && (
            <Link to="/shop/category/$category" params={{ category: product.category_slug }} className="mb-2 inline-block text-xs font-medium uppercase tracking-wider text-[var(--gold-deep)] hover:underline">
              {categoryLabel(product.category_slug)}
            </Link>
          )}
          {product.product_type === "affiliate" ? null : (
            <p className="mt-4 text-2xl">
              <ShopPrice p={product} />
            </p>
          )}

          {product.description ? (
            <div className="mt-6 whitespace-pre-line leading-relaxed text-foreground/90">
              {product.description}
            </div>
          ) : null}

          {product.details ? (
            <div className="mt-6 rounded-xl border bg-card p-4">
              <h2 className="mb-2 text-sm font-semibold">Details</h2>
              <ul className="space-y-1 text-sm text-foreground/90">
                {product.details.split("\n").filter(Boolean).map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          ) : null}

          {product.product_type === "affiliate" && product.affiliate_url ? (
            <div className="mt-8 space-y-3">
              <a
                href={product.affiliate_url}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={() => void trackClick({ data: { slug } }).catch(() => {})}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-primary px-6 font-medium text-primary-foreground transition hover:opacity-90"
              >
                View on Amazon <ExternalLink className="h-4 w-4" aria-hidden="true" />
              </a>
              <p className="text-center text-xs text-muted-foreground">{AFFILIATE_DISCLOSURE}</p>
            </div>
          ) : hideCommerce ? (
            <p className="mt-8 rounded-xl border bg-card p-4 text-sm text-muted-foreground">
              This item is available on our website.
            </p>
          ) : (
            <div className="mt-8 space-y-3">
              <Button size="lg" className="w-full" onClick={buy} disabled={busy}>
                {busy ? (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Download className="me-2 h-4 w-4" aria-hidden="true" />
                )}
                Buy and download
              </Button>
              <p className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Secure payment. Your download link appears straight after paying.
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
